import { Box, Button, Flex, IconButton } from '@ouster/webviz-react-components';

import { useEffect, useState } from 'react';
import { GdprDataCheckPayload } from 'src/reducers/adminDashboard';

import DashboardCard from './DashboardCard';
import userLookupStyle from './userLookup.module.css';

const DELETE_CONFIRMATION_TEXT = 'DELETE USER DATA';

export default function UserDelete({
  handleSearch,
  handleDelete,
  clearGdprInfo,
  gdprInfo,
}: {
  handleSearch: (email: string) => Promise<void>;
  handleDelete: (email: string) => Promise<boolean>;
  clearGdprInfo: () => void;
  gdprInfo: GdprDataCheckPayload | null;
}) {
  const [email, setEmail] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');

  useEffect(() => {
    if (email.trim() === '') {
      clearGdprInfo();
    }
  }, [email, clearGdprInfo]);

  const onSearchHandler = () => {
    if (email.trim()) {
      handleSearch(email.trim());
    }
  };
  const onDeleteHandler = async () => {
    if (email.trim()) {
      const isDeleted = await handleDelete(email.trim());
      if (isDeleted) {
        setEmail('');
        setDeleteConfirmation('');
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onSearchHandler();
    }
  };

  const isDeleteDisabled =
    deleteConfirmation !== DELETE_CONFIRMATION_TEXT || gdprInfo === null;

  const searchContent = (
    <Box
      display="grid"
      gap={2}
      alignItems="center"
      mt={3}
      data-testid="user-lookup-search-box"
    >
      <Box display="flex" gap={2} alignItems="center">
        <Box position="relative" flexGrow={1}>
          <input
            placeholder="Enter email address"
            value={email}
            autoComplete="off"
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={handleKeyPress}
            type="email"
            className={userLookupStyle.inputField}
            data-testid="user-gdpr-lookup-search-input"
          />
          <IconButton
            position="absolute"
            right={1}
            top={1}
            icon="close"
            variant="ghost"
            size="sm"
            emphasis="quiet"
            onClick={() => setEmail('')}
          />
        </Box>
        <Button
          variant="outline"
          onClick={onSearchHandler}
          isDisabled={!email.trim()}
          data-testid="gdpr-user-search-button"
        >
          Search
        </Button>
      </Box>
      <Box
        mt={4}
        color="red.200"
      >{`Type "${DELETE_CONFIRMATION_TEXT}" to confirm deletion`}</Box>
      <Flex gap={2}>
        <input
          type="text"
          placeholder={DELETE_CONFIRMATION_TEXT}
          value={deleteConfirmation}
          className={userLookupStyle.inputField}
          onChange={(e) => setDeleteConfirmation(e.target.value)}
        />
        <Button
          variant="outline"
          colorScheme={isDeleteDisabled ? 'brand' : 'red'}
          onClick={onDeleteHandler}
          isDisabled={isDeleteDisabled}
          data-testid="gdpr-user-delete-button"
          px={4}
        >
          Delete
        </Button>
      </Flex>
    </Box>
  );

  return (
    <DashboardCard
      title="Delete User"
      description="Enter an email address to search the user you want to delete"
      data-testid="user-delete-card"
    >
      {searchContent}
    </DashboardCard>
  );
}
